/** Meeting Scorer dates and times are US Eastern. */
export const MEETING_TIMEZONE = 'America/New_York';
const pad = (n) => String(n).padStart(2, '0');
/** Today in US Eastern as YYYY-MM-DD (en-CA formats as ISO date). */
export const etTodayYmd = () => new Intl.DateTimeFormat('en-CA', { timeZone: MEETING_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());
export const ymdFromCalendarDate = (d) => `${d.year}-${pad(d.month)}-${pad(d.day)}`;
/** "2:30 PM" / "02:30pm" / "11:05 am" / "10 AM" / "10pm" — minutes optional; the same rule the server applies. */
export const isValidMeetingTime = (text) => /^(1[0-2]|0?[1-9])(?::([0-5]\d))?\s*([AaPp])\.?\s*[Mm]\.?$/.test(String(text ?? '').trim());
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/**
 * YYYY-MM-DD → "Oct 7, 2026", read from the string itself: these are ET
 * calendar days, and parsing one into a Date would shift it a day for
 * browsers west of UTC.
 */
export const fmtLongYmd = (ymd) => {
    if (!ymd)
        return '—';
    const [y, m, d] = ymd.split('-').map(Number);
    return y && m && d ? `${MONTHS[m - 1]} ${d}, ${y}` : ymd;
};
/** YYYY-MM-DD → "10/07/2026". */
export const fmtShortYmd = (ymd) => {
    if (!ymd)
        return '—';
    const [y, m, d] = ymd.split('-');
    return y && m && d ? `${m}/${d}/${y}` : ymd;
};
/** Why a meeting has no transcript / did not happen — same list as the email-link page. */
export const NO_SHOW_REASONS = [
    { value: 'no_show', label: 'No Show' },
    { value: 'fireflies_not_joined', label: "Fireflies didn't join" },
    { value: 'client_rescheduled', label: 'Client rescheduled' },
    { value: 'client_ooo', label: 'Client OOO' },
    { value: 'am_ooo', label: 'AM OOO' },
];
/** Not processed by the scorer yet — the meeting can still be edited. */
export const isWaitingStatus = (s) => s === 'pending' || s === 'needs_details';
export const SCORING_STATUS_LABEL = {
    pending: { label: 'Pending', color: '#344054', bg: '#F2F4F7' },
    /** Calendar meeting whose name matched no client — not scored until the AM fixes it */
    needs_details: { label: 'Needs Details', color: '#B54708', bg: '#FFFAEB' },
    /** Submitted in the nightly batch, report on its way */
    scoring: { label: 'Scoring…', color: '#175CD3', bg: '#EFF8FF' },
    scored: { label: 'Scored', color: '#027A48', bg: '#ECFDF3' },
    // Stored status is still no_show; the meeting did not take place / has no transcript.
    no_show: { label: 'Not Held', color: '#B54708', bg: '#FFFAEB' },
    failed: { label: 'Failed', color: '#B42318', bg: '#FEF3F2' },
    /** Shown instead of "Pending" for a meeting marked not eligible to score */
    ineligible: { label: 'Not Eligible', color: '#5925DC', bg: '#F4F3FF' },
};
/** Saves a blob response from ApiCall, using the server's filename when it sent one. */
export function saveBlob(response, fallbackName) {
    const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'] || '';
    const named = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = named || fallbackName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}
/** ISO timestamp → "Oct 9, 2026, 3:15 PM" in the viewer's time zone; '' if missing. */
export const fmtDateTime = (iso) => {
    if (!iso)
        return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
